import React from "react";
import { FormattedMessage } from "react-intl";
import { TransitionGroup } from "react-transition-group";

import { Engine, getWordDefinition, GlossaryWordModel } from "@courses-pm2/api";
import { hrtime } from "@use/universal-hrtime";

import { isAndroid, isIOS } from "@apps/viewer/src/ts/utils";

import { Fade } from "@apps/viewer/src/ts/components/pm2/GlossaryWord/Fade";
import { Tooltip } from "@apps/viewer/src/ts/components/utils/Tooltip/Tooltip";

type GlossaryWordProps = {
    /**
     * The model contains the contents and the desired configurations of the
     * component.
     */
    model: GlossaryWordModel;

    /**
     * Will contain a reference to the engine that was used when fetching the
     * glossary word.
     */
    engine?: Engine;
};

type GlossaryWordState = {
    /**
     * Specifies if the mouse is currently over the word (which means that the
     * definition should be opened).
     */
    isMouseOver?: boolean;

    /**
     * Will contain the definition to display for this word, when the user
     * hovers the word.
     */
    definition?: React.ReactNode;
};

/**
 * Glossary words will need to resolve the definition of the word when the user
 * puts the cursor over it, so a tooltip can be displayed.
 */
export class GlossaryWord extends React.PureComponent<GlossaryWordProps, GlossaryWordState> {
    /**
     * Set up initial state.
     */
    public readonly state: GlossaryWordState = {};

    /**
     * Will contain a reference to the wrapper, which is going to toggle the
     * word definition on.
     */
    protected wrapperRef: React.RefObject<HTMLSpanElement | null> = React.createRef();

    /**
     * Will contain a reference to any currently running requests to fetch the
     * definition of a word.
     */
    protected definitionRequest: Promise<React.ReactNode> | undefined;

    /**
     * Will contain a reference to the timeout that's used to defer opening of
     * the word definitions slightly after having hovered a glossary word.
     */
    protected mouseOverTimeout: number;

    /**
     * Primary render method.
     */
    public render(): React.JSX.Element {
        const { isMouseOver, definition } = this.state;

        // Determine if we should use touch events?
        const isTouch = isAndroid() || isIOS();

        return (
            // tslint:disable use-simple-attributes
            <span
                ref={this.wrapperRef}
                className="glossaryWord"
                onMouseEnter={!isTouch ? this.onMouseEnter : undefined}
                onMouseLeave={!isTouch ? this.onMouseLeave : undefined}
                onTouchStart={isTouch ? this.onMouseEnter : undefined}
                onTouchEnd={isTouch ? this.onMouseLeave : undefined}
                onTouchCancel={isTouch ? this.onMouseLeave : undefined}
                onClick={isTouch ? this.onMouseEnter : undefined}
                role="term"
            >
                {this.props.model.render()}

                <Tooltip
                    active={!!isMouseOver}
                    tooltipWidth={240}
                    targetRef={this.wrapperRef}
                >
                    <span className="glossaryWord__definition">
                        <TransitionGroup component={null /* tslint:disable-line no-null-keyword */}>
                            {
                                definition
                                    ? this.renderDefinition(definition)
                                    : this.renderPreloader()
                            }
                        </TransitionGroup>
                    </span>
                </Tooltip>

                {isTouch && !!isMouseOver
                    ? <div className="glossaryWord__eventCatcher" onClick={this.onMouseLeave} role="none" />
                    : undefined
                }

                <style jsx>{`
                    .glossaryWord {
                        cursor: default;
                        color: #dc4320;

                        &:hover {
                            text-decoration: underline;
                        }
                    }

                    .glossaryWord__definition {
                        /** Allow position: relative during crossfade */
                        position: relative;

                        /** Set up sizing of the definition tooltip */
                        display: block;
                        box-sizing: border-box;
                        width: 240px;

                        /** Apply internal padding */
                        padding: calc(10px - 0.15em) 10px;

                        /** Apply text styling */
                        line-height: 1.3em;
                        min-height: 1.3em;
                    }

                    .glossaryWord__eventCatcher {
                        position: fixed;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        top: 0;
                        z-index: 100;
                    }
                `}</style>
            </span>
            // tslint:enable use-simple-attributes
        );
    }

    /**
     * Helper that'll render a preloader, which is going to be displayed while
     * we're waiting for the actual definition to be fetched.
     */
    protected renderPreloader(): React.JSX.Element {
        return (
            <Fade key="preloader">
                <FormattedMessage
                    id="GlossaryWord__preloading"
                    defaultMessage="Indlæser"
                />
                <i>.</i><i>.</i><i>.</i>

                <style jsx>{`
                    i {
                        /** Prepare fade in-out animation */
                        animation: fadeDot 1.2s linear backwards infinite;

                        /** Defer dot 2 and 3 */
                        &:nth-child(3) {
                            animation-delay: 0.2s;
                        }

                        &:nth-child(4) {
                            animation-delay: 0.4s;
                        }
                    }

                    /** Fade bullets in / out of view */
                    @keyframes fadeDot {
                        0% {
                            opacity: 0;
                        }

                        16.66% {
                            opacity: 1;
                        }

                        50% {
                            opacity: 1;
                        }

                        66.66% {
                            opacity: 0;
                        }

                        100% {
                            opacity: 0;
                        }
                    }
                `}</style>
            </Fade>
        );
    }

    /**
     * Helper that'll render the actual definition of the word, once it is
     * available for display.
     */
    protected renderDefinition(definition: React.ReactNode): React.JSX.Element {
        return (
            <Fade key="definition">{definition}</Fade>
        );
    }

    /**
     * Listener that's triggered when the user moves the mouse over the glossary
     * word, making sure that the tooltip will be activated.
     */
    protected onMouseEnter = (evt: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
        // Ensure that events doesn't propagate to root since onMouseLeave will be
        // triggered on touch devices if a (click) events reaches the root. In case
        // the glossaryWord wrapper was clicked, then we don't want to close the
        // tooltip, instead we want to open
        evt.stopPropagation();

        const { engine, model } = this.props;

        // Register that the definition should be displayed
        window.clearTimeout(this.mouseOverTimeout);

        this.mouseOverTimeout = window.setTimeout(
            () => { this.setState({ isMouseOver: true }); },
            300,
        );

        if (!this.state.definition && !this.definitionRequest) {
            // Register when we started to fetch the word definition
            const startedAt = hrtime();

            // If the word definition hasn't been fetched yet, then do so now...
            this.definitionRequest = (
                engine
                    ? engine.getWordDefinition({
                        wordId: model.id,
                        portalId: model.portalId,
                    })
                    : getWordDefinition({
                        wordId: model.id,
                        portalId: model.portalId,
                    })
            );

            // Handle response
            this.definitionRequest.then(
                // Display definition once it's fetched
                (definition) => {
                    setTimeout(
                        () => { this.setState({ definition }); },
                        Math.max(0, 1000 - (hrtime() - startedAt)),
                    );
                },

                // ... Or close the tooltip instantly if we were unable to load
                // it!
                (err) => {
                    this.onMouseLeave();
                    this.definitionRequest = undefined;

                    // tslint:disable-next-line no-console
                    console.error(err);
                },
            );
        }
    }

    /**
     * Listener that's triggered when the user moves the mouse away from the
     * glossary word, closing the tooltip again.
     */
    protected onMouseLeave = (evt?: React.MouseEvent | React.TouchEvent) => {
        if (evt) {
            evt.stopPropagation();
        }

        window.clearTimeout(this.mouseOverTimeout);

        this.mouseOverTimeout = window.setTimeout(
            () => { this.setState({ isMouseOver: false }); },
            100,
        );
    }
}
